import { FC, useMemo, useState } from 'react';

import { GiftDeliveryForAdmin } from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  ThanksPrintTemplate,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Image } from '@shared/api/media/media.interface';

import { GiftThanksPrintPreview } from '../../giftStore/containers/GiftThanksPrintSection/GiftThanksPrintPreview';

interface Props {
  delivery: GiftDeliveryForAdmin | null;
  templates: ThanksPrintTemplate<Image>[];
  onClose: () => void;
}

/** 클립보드 복사 (보안 컨텍스트가 아닌 환경 대비 fallback 포함) */
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // execCommand fallback 으로 진행
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

// 감사글 / 보내는분을 PSD 등에 바로 붙여넣을 수 있도록 복사 가능한 필드
const CopyField: FC<{ label: string; value: string; multiline?: boolean }> =
  function CopyField({ label, value, multiline }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      const ok = await copyToClipboard(value || '');
      if (!ok) {
        alert('복사에 실패했습니다. 직접 선택하여 복사해 주세요.');
        return;
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    };

    return (
      <div className="overflow-hidden rounded-lg border bg-gray-50">
        <div className="flex items-center justify-between border-b bg-white px-3 py-2">
          <span className="text-xs font-semibold text-gray-600">{label}</span>
          <button
            onClick={handleCopy}
            className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
              copied
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
            }`}
          >
            {copied ? '복사됨 ✓' : '복사'}
          </button>
        </div>
        <p
          className={`px-3 py-2 text-sm text-gray-800 ${
            multiline ? 'min-h-[3rem] whitespace-pre-wrap' : ''
          }`}
        >
          {value || '-'}
        </p>
      </div>
    );
  };

// 감사카드 미리보기 + 인쇄 (주문 시 작성한 메시지/보내는분을 템플릿 위에 얹어 표시)
export const GiftBrandThanksCardModal: FC<Props> = function GiftBrandThanksCardModal({
  delivery,
  templates,
  onClose,
}) {
  const tp = delivery?.thanksPrint;

  // 주문에 기록된 templateId로 브랜드 템플릿 레이아웃 매칭
  const template = useMemo(() => {
    if (!tp?.templateId) return undefined;
    return templates.find((t) => t._id === tp.templateId);
  }, [templates, tp?.templateId]);

  if (!delivery || !tp) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-base font-bold">감사카드 인쇄</h3>
          <button
            onClick={onClose}
            aria-label="닫기"
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto p-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            {/* 이미지 미리보기 (인쇄 대상) */}
            {template ? (
              <div id="thanks-card-print" className="w-full shrink-0 md:w-1/2">
                <GiftThanksPrintPreview
                  template={template}
                  message={tp.message}
                  sender={tp.senderName}
                />
              </div>
            ) : (
              <div className="w-full rounded-lg bg-gray-50 p-6 text-center text-sm text-gray-500 md:w-1/2">
                감사카드 템플릿을 찾을 수 없습니다.
              </div>
            )}

            {/* 복사 패널 — PSD 등에 붙여넣어 인쇄할 수 있도록 텍스트 제공 */}
            <div className="w-full space-y-3 md:w-1/2">
              <p className="text-xs text-gray-400">
                PSD 등에 붙여넣어 인쇄할 수 있도록 복사하세요.
              </p>
              <CopyField label="감사글" value={tp.message} multiline />
              <CopyField label="보내는 분 (배상)" value={tp.senderName} />
            </div>
          </div>
        </div>

        <div className="flex gap-2 border-t px-4 py-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
          >
            닫기
          </button>
          <button
            onClick={() => window.print()}
            disabled={!template}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            인쇄하기
          </button>
        </div>
      </div>

      {/* 인쇄 시 감사카드만 출력 */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #thanks-card-print, #thanks-card-print * { visibility: visible !important; }
          #thanks-card-print {
            position: fixed !important;
            top: 0; left: 0; right: 0;
            margin: 0 auto !important;
            width: 100% !important;
            max-width: 600px !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          @page { margin: 12mm; }
        }
      `}</style>
    </div>
  );
};
